import { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import Footer from '../../components/shared/Footer';
import { initializeDentuluProButton } from '../../utils/dentuluProSSO';
import { loadRecaptcha, executeRecaptcha, showRecaptchaBadge, hideRecaptchaBadge } from '../../utils/recaptcha';

export default function Login() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchParams] = useSearchParams();

  // Google OAuth Client ID from environment variable
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  // Initialize Dentulu Pro button and load reCAPTCHA on mount
  useEffect(() => {
    const cleanup = initializeDentuluProButton('dentuluprologinbtn', 'login');
    // Load reCAPTCHA script and show badge
    loadRecaptcha().catch(err => console.warn('Failed to load reCAPTCHA:', err));
    showRecaptchaBadge();
    return () => {
      cleanup();
      hideRecaptchaBadge();
    };
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.email || !form.password) {
      setError('Please fill in both email and password');
      return;
    }

    setIsSubmitting(true);

    try {
      // Get reCAPTCHA token
      const recaptchaToken = await executeRecaptcha('LOGIN');
      
      const res = await api.post('/api/login', {
        ...form,
        recaptcha_token: recaptchaToken
      });
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('role', res.data.role);

      const returnUrl = searchParams.get('return');

      // Full page load (not a client-side navigate) so every app-level
      // context (company switcher, permissions, profile, etc.) remounts
      // fresh for this session instead of carrying over stale state.
      window.location.href = returnUrl || (res.data.role === 'admin' ? '/admin' : '/dashboard');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Login failed. Please try again.';

      // Handle brute-force protection errors
      if (errorMessage === 'ACCOUNT_LOCKED_RESET_REQUIRED') {
        setError(
          <div>
            <div className="font-semibold text-red-800 mb-2">Account locked — password reset required</div>
            <div>Your account has been locked due to too many failed login attempts. You must reset your password before you can sign in again.</div>
            <Link to="/forgot-password" className="block mt-3 text-blue-600 hover:underline font-medium">
              Reset your password →
            </Link>
          </div>
        );
      } else if (errorMessage.startsWith('ACCOUNT_TEMPORARILY_LOCKED:')) {
        const minutes = errorMessage.split(':')[1] || '15';
        setError(
          <div>
            <div className="font-semibold text-red-800 mb-2">Account temporarily locked</div>
            <div>Too many failed login attempts. Please try again in {minutes} minutes.</div>
          </div>
        );
      // Show helpful message for SSO users
      } else if (errorMessage.includes('SSO login')) {
        setError(
          <div>
            {errorMessage}
            <div className="mt-2 text-sm">
              Please use one of the SSO options below to sign in.
            </div>
          </div>
        );
      } else if (errorMessage.includes('reCAPTCHA')) {
        setError('Security verification failed. Please try again.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 py-8">
        <div className="bg-white shadow-lg rounded-lg px-8 pt-6 pb-8 w-full max-w-md">
          {/* Header Links */}
          <div className="flex justify-between items-center mb-6">
            <Link to="/" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Marketplace
            </Link>
            <Link to="/portal" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
              Developer Portal
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>

          <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
            Login
          </h2>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                type="email"
                name="email"
                value={form.email}
                placeholder="Email"
                onChange={handleChange}
                required
              />
            </div>

            {/* Password */}
            <div>
              <div className="relative">
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={form.password}
                  placeholder="Password"
                  onChange={handleChange}
                  maxLength={15}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <Eye className="w-5 h-5" />
                  ) : (
                    <EyeOff className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Forgot Password */}
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-sm text-blue-600 hover:underline">
                Forgot your password?
              </Link>
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-blue-600 text-white py-3 rounded-md hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">— Or sign in with —</span>
            </div>
          </div>

          {/* SSO Options */}
          <div className="space-y-3">
            {/* Google Sign-In - Only show if configured */}
            {!GOOGLE_CLIENT_ID && (
              <div className="text-center text-sm text-gray-500 mb-4">
                Google Sign-In not configured
              </div>
            )}

            {/* Dentulu Pro SSO Button */}
            <a
              href="#"
              id="dentuluprologinbtn"
              className="block w-full bg-white border-2 border-blue-500 hover:bg-blue-50 hover:border-blue-600 transition-all rounded-lg overflow-hidden shadow-sm hover:shadow-md"
            >
              <div className="flex items-center justify-center py-3 px-4">
                <img
                  src="/dentulu-pro-icon.png"
                  alt="Dentulu Pro"
                  className="h-8"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const parent = e.target.parentElement;
                    if (!parent.querySelector('.fallback-text')) {
                      const span = document.createElement('span');
                      span.className = 'text-blue-600 font-semibold text-base fallback-text flex items-center gap-2';
                      span.innerHTML = `
                        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                        </svg>
                        Sign in with Dentulu Pro
                      `;
                      parent.appendChild(span);
                    }
                  }}
                />
              </div>
            </a>
          </div>

          {/* Don't have account */}
          <div className="text-center text-sm text-gray-600 mt-6">
            Don't have an account?{' '}
            <Link to="/signup" className="text-blue-600 hover:underline font-medium">
              Sign up
            </Link>
          </div>

          {/* Browse Marketplace Link */}
          <div className="text-center mt-4">
            <Link to="/" className="text-sm text-blue-600 hover:underline flex items-center justify-center gap-1">
              Browse Marketplace
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}